import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalHeader from '../ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';
import { useToast } from '../../contexts/ToastContext';
import { usePrintDefaults } from '../../hooks/usePrintDefaults';
import { printErrorMessage, type Printer, type PrinterType, type PrintJobResult, type PrintOptions } from '../../services/printing.service';
import PrinterSelect from './PrinterSelect';
import { lastUsedPrinter } from './lastUsedPrinter';

export interface PrintDialogProps {
  open: boolean;
  type: PrinterType;
  title?: string;
  onClose: () => void;
  /** Sends the job. Resolves with the server's result or rejects with the axios error. */
  onPrint: (opts: Required<PrintOptions>) => Promise<PrintJobResult>;
}

const MAX_COPIES = 20;

/**
 * Pick a printer and go. Preselection order: the printer this type was last
 * sent to in this session, then the user's saved default, then nothing (the
 * dialog asks). Copies only matter for label queues; CUPS laser queues honour
 * the IPP copies attribute but V1 never offered it, so neither do we.
 */
const PrintDialog: React.FC<PrintDialogProps> = ({ open, type, title = 'Print', onClose, onPrint }) => {
  const { showToast } = useToast();
  const { defaults, loading: defaultsLoading } = usePrintDefaults();
  const [printer, setPrinter] = useState('');
  // The copies box is free text while typing so "15" can be entered after a clear;
  // it is clamped to 1..MAX_COPIES on blur and again on submit.
  const [copiesText, setCopiesText] = useState('1');
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [activeNames, setActiveNames] = useState<Set<string> | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Preselect once the defaults are known, unless the user already picked.
  useEffect(() => {
    if (!open) { setTouched(false); setPrinter(''); setCopiesText('1'); setActiveNames(null); return; }
    if (touched || defaultsLoading) return;
    const remembered = lastUsedPrinter.get(type);
    const saved = type === 'LASER' ? defaults.laser : defaults.label;
    setPrinter(remembered || saved || '');
  }, [open, type, defaults, defaultsLoading, touched]);

  // A remembered or saved printer that has since been deleted or retyped must
  // not stay preselected: once the active list is known, drop anything not in it.
  useEffect(() => {
    if (!activeNames || !printer) return;
    if (!activeNames.has(printer)) setPrinter('');
  }, [activeNames, printer]);

  // Escape closes (repo convention, see DraggableModal), never mid-send. Registered in
  // the CAPTURE phase and swallowed: this dialog stacks on top of surfaces that have
  // their own window Escape → close (the document preview lightbox), and one keypress
  // must close only the topmost layer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (!busy) onClose();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [open, busy, onClose]);

  // Move focus into the dialog: the printer trigger, else the Print button.
  useEffect(() => {
    if (!open) return;
    const root = bodyRef.current;
    if (!root) return;
    const target = root.querySelector<HTMLElement>('button[aria-label="Printer"]')
      ?? root.querySelector<HTMLElement>('button[data-print-submit]');
    target?.focus();
  }, [open]);

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const clampCopies = (text: string): number =>
    Math.min(MAX_COPIES, Math.max(1, parseInt(text, 10) || 1));

  const commitCopies = () => {
    const n = clampCopies(copiesText);
    setCopiesText(String(n));
    return n;
  };

  const onPrintersLoaded = (rows: Printer[]) =>
    setActiveNames(new Set(rows.map((p) => p.installname)));

  const handlePrint = async () => {
    if (!printer || busy) return;
    const n = type === 'LABEL' ? commitCopies() : 1;
    setBusy(true);
    try {
      const result = await onPrint({ printer, copies: n });
      lastUsedPrinter.set(type, result.printer || printer);
      const copyNote = result.copies > 1 ? ` × ${result.copies}` : '';
      showToast({ type: 'success', message: `Sent to ${result.display_name}${copyNote} (job ${result.job_id})` });
      onClose();
    } catch (err) {
      showToast({ type: 'error', message: printErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-scrim/50" onClick={busy ? undefined : onClose} />
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative bg-cmp-modal rounded-lg shadow-xl w-full max-w-sm mx-4"
      >
        <ModalHeader title={title} onClose={busy ? undefined : onClose} dragHandleProps={dragHandleProps} />
        <div ref={bodyRef} className="p-6 space-y-4">
          <div>
            <span className="text-sm text-label block mb-1">Printer</span>
            <PrinterSelect
              type={type}
              value={printer}
              onChange={(v) => { setTouched(true); setPrinter(v); }}
              disabled={busy}
              onLoaded={onPrintersLoaded}
            />
          </div>
          {type === 'LABEL' && (
            <div>
              <label htmlFor="print-copies" className="text-sm text-label block mb-1">Copies</label>
              <input
                id="print-copies"
                type="number"
                min={1}
                max={MAX_COPIES}
                value={copiesText}
                disabled={busy}
                onChange={(e) => setCopiesText(e.target.value)}
                onBlur={commitCopies}
                className="w-24 px-3 py-2 text-sm border border-theme-border-primary rounded-lg bg-theme-bg-primary text-body focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={!printer || busy}
              data-print-submit
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Sending…' : 'Print'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PrintDialog;
